import { normalizeVideoSource } from '../../../jquery.fitvids/normalize-video-source';
import type { VendorDocument, VendorHTMLVideoElement, VideoContentInfo } from './types';

const VIDEO_OVERLAY_CLASS = 'et_pb_video_overlay';

/**
 * Delay before MultiView may run after document fullscreen ends.
 *
 * Android/Samsung teardown still fires resize/orientation after `fullscreenElement` is already null.
 * Processing MultiView in that window mutates the iframe tree and can leave fullscreen stuck.
 *
 * @since ??
 */
const MULTIVIEW_FULLSCREEN_EXIT_SETTLE_MS = 500;

/**
 * Parse video markup into a normalized content descriptor.
 *
 * @since ??
 *
 * @param {string} html Video markup HTML string.
 *
 * @returns {VideoContentInfo} Parsed video content info.
 */
const parseVideoContentHtml = (html: string): VideoContentInfo => {
  const template = document.createElement('template');
  template.innerHTML = html.trim();

  const iframe = template.content.querySelector('iframe');

  if (iframe) {
    const src = iframe.getAttribute('src') ?? iframe.getAttribute('data-src') ?? '';

    return {
      type: 'iframe',
      sources: [normalizeVideoSource(src)],
    };
  }

  const video = template.content.querySelector('video');

  if (video) {
    const sources = Array.from(video.querySelectorAll('source')).map(source =>
      normalizeVideoSource(source.getAttribute('src') ?? ''),
    );

    return {
      type: 'video',
      sources,
    };
  }

  return {
    type: 'unknown',
    sources: [],
  };
};

/**
 * Get normalized video content from a live video box element.
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 *
 * @returns {VideoContentInfo} Current video content info.
 */
const getVideoContentFromElement = (element: HTMLElement): VideoContentInfo => {
  const wrapper = element.querySelector('.fluid-width-video-wrapper');
  const html = wrapper ? wrapper.innerHTML : element.innerHTML;

  return parseVideoContentHtml(html);
};

/**
 * Get normalized video content from MultiView state data HTML.
 *
 * @since ??
 *
 * @param {string} stateData MultiView content HTML string.
 *
 * @returns {VideoContentInfo} Parsed state video content info.
 */
const getVideoContentFromStateData = (stateData: string): VideoContentInfo => parseVideoContentHtml(stateData);

/**
 * Determine whether video box content should be replaced for a breakpoint swap.
 *
 * Mirrors D4 `callbackHandlerVideo` source comparison semantics.
 *
 * @since ??
 *
 * @param {VideoContentInfo} currentContent Current live video content.
 * @param {VideoContentInfo} newContent Incoming breakpoint video content.
 *
 * @returns {boolean} Whether DOM replacement is required.
 */
const isVideoContentUpdateNeeded = (currentContent: VideoContentInfo, newContent: VideoContentInfo): boolean => {
  if ('unknown' === currentContent.type || 'unknown' === newContent.type) {
    return true;
  }

  if (currentContent.type !== newContent.type) {
    return true;
  }

  if ('iframe' === currentContent.type) {
    return currentContent.sources[0] !== newContent.sources[0];
  }

  if (currentContent.sources.length !== newContent.sources.length) {
    return true;
  }

  return currentContent.sources.some((source, index) => source !== newContent.sources[index]);
};

/**
 * Check whether state data contains an iframe embed.
 *
 * @since ??
 *
 * @param {string} stateData MultiView content HTML string.
 *
 * @returns {boolean} Whether state data is an iframe embed.
 */
const stateDataContainsIframe = (stateData: string): boolean =>
  'iframe' === getVideoContentFromStateData(stateData).type;

/**
 * Get the current fullscreen element across vendor-prefixed APIs.
 *
 * @since ??
 *
 * @returns {Element | null} The fullscreen element, if any.
 */
const getFullscreenElement = (): Element | null => {
  const vendorDocument: VendorDocument = document;

  return (
    vendorDocument.fullscreenElement ??
    vendorDocument.webkitFullscreenElement ??
    vendorDocument.msFullscreenElement ??
    null
  );
};

/**
 * Check whether the document is currently in fullscreen.
 *
 * @since ??
 *
 * @returns {boolean} Whether the document has a fullscreen element.
 */
const isDocumentInFullscreen = (): boolean => null !== getFullscreenElement();

/**
 * Decide whether MultiView breakpoint processing should wait out fullscreen.
 *
 * Lock while document fullscreen is active, and for a short settle window after exit,
 * so resize/fullscreenchange cannot swap content during Android teardown.
 *
 * @since ??
 *
 * @param {boolean} isInitInvoked Whether MultiView has completed its first run.
 * @param {number} fullscreenExitedAt Timestamp when fullscreen last ended, or 0.
 * @param {number} now Current timestamp used to measure the settle window.
 *
 * @returns {boolean} Whether MultiView processing should be skipped.
 */
const shouldLockMultiViewProcessing = (
  isInitInvoked: boolean,
  fullscreenExitedAt: number,
  now: number = Date.now(),
): boolean => {
  if (!isInitInvoked) {
    return false;
  }

  if (isDocumentInFullscreen()) {
    return true;
  }

  return 0 !== fullscreenExitedAt && now - fullscreenExitedAt < MULTIVIEW_FULLSCREEN_EXIT_SETTLE_MS;
};

/**
 * Check whether a video box or one of its descendants is in fullscreen.
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 *
 * @returns {boolean} Whether the video box is in fullscreen.
 */
const isVideoBoxInFullscreen = (element: HTMLElement): boolean => {
  const fullscreenElement = getFullscreenElement();

  if (null !== fullscreenElement) {
    return element === fullscreenElement || element.contains(fullscreenElement);
  }

  const video = element.querySelector('video');

  if (!video) {
    return false;
  }

  const vendorVideo: VendorHTMLVideoElement = video;

  return true === vendorVideo.webkitDisplayingFullscreen;
};

/**
 * Check whether an HTML5 video inside the box is currently playing.
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 *
 * @returns {boolean} Whether HTML5 playback is active.
 */
const hasHtml5VideoPlaybackStarted = (element: HTMLElement): boolean => {
  const video = element.querySelector('video');

  if (!video) {
    return false;
  }

  return !video.paused && !video.ended;
};

/**
 * Check whether overlay playback has started (overlay dismissed via play click).
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 *
 * @returns {boolean} Whether the sibling overlay is hidden after play.
 */
const hasOverlayPlaybackStarted = (element: HTMLElement): boolean => {
  const overlay = element.nextElementSibling;

  if (!(overlay instanceof HTMLElement) || !overlay.classList.contains(VIDEO_OVERLAY_CLASS)) {
    return false;
  }

  return 'none' === overlay.style.display;
};

/**
 * Determine whether a video box should keep its current player instead of swapping content.
 *
 * Android fullscreen/orientation can change the MultiView breakpoint (phone → tablet/desktop).
 * Replacing the player in that moment destroys playback even when the source change is genuine.
 *
 * @since ??
 *
 * @param {HTMLElement} element The `.et_pb_video_box` element.
 *
 * @returns {boolean} Whether the current video DOM should be preserved.
 */
const shouldPreserveVideoBoxContent = (element: HTMLElement): boolean =>
  isVideoBoxInFullscreen(element) || hasHtml5VideoPlaybackStarted(element) || hasOverlayPlaybackStarted(element);

export {
  getFullscreenElement,
  getVideoContentFromElement,
  getVideoContentFromStateData,
  isDocumentInFullscreen,
  isVideoContentUpdateNeeded,
  MULTIVIEW_FULLSCREEN_EXIT_SETTLE_MS,
  shouldLockMultiViewProcessing,
  shouldPreserveVideoBoxContent,
  stateDataContainsIframe,
};
